import { NavLink, useLocation } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import {
  Home as HomeIcon,
  FileText,
  Shield,
  Users,
  Building,
  HardDrive,
  Settings as SettingsIcon,
  Plug,
  Briefcase,
  Key,
  Sliders,
  Bell,
  BookOpen,
  LogOut,
} from 'lucide-react'
import { useAuth } from './AuthProvider'
import { useI18n } from '../i18n'
import { Button } from './ui/Button'

export type MenuItem = {
  label: string
  to: string
  icon: LucideIcon
  roles: Array<'user' | 'admin'>
  permission?: string
}

export const menuConfig: MenuItem[] = [
  { label: 'Home', icon: HomeIcon, to: '/', roles: ['user', 'admin'] },
  { label: 'Reports', icon: FileText, to: '/reports', roles: ['user', 'admin'], permission: 'access.reports' },
  { label: 'Compliance Center', icon: Shield, to: '/compliance-center/overview', roles: ['user', 'admin'], permission: 'access.compliance' },
  { label: 'TPSM', icon: Building, to: '/tpsm/inventory', roles: ['user', 'admin'], permission: 'access.suppliers' },
  { label: 'Assets', icon: HardDrive, to: '/assets', roles: ['user', 'admin'], permission: 'access.assets' },
  { label: 'Notifications', icon: Bell, to: '/notifications', roles: ['user', 'admin'], permission: 'access.notifications' },
  { label: 'User Settings', icon: SettingsIcon, to: '/settings', roles: ['user', 'admin'] },
  // Org OWNER/ADMIN manage members here — not gated on global platform admin role
  { label: 'Organization Settings', icon: Users, to: '/organization-settings', roles: ['user', 'admin'] },
  { label: 'Integrations', icon: Plug, to: '/integrations', roles: ['user', 'admin'], permission: 'access.integrations' },
  { label: 'Organizations', icon: Briefcase, to: '/organizations', roles: ['admin'] },
  { label: 'Admin Panel: User & Access Management', icon: Key, to: '/admin', roles: ['admin'] },
  { label: 'Settings: User Preferences & System Configuration', icon: Sliders, to: '/admin/settings', roles: ['admin'] },
  { label: 'Starter guide', icon: BookOpen, to: '/starter-guide', roles: ['user', 'admin'] },
]

export interface SidebarNavContentProps {
  /** Called after a nav item is activated (e.g. close drawer). */
  onNavigate?: () => void
  /** Extra class on the outer wrapper (drawer vs fixed sidebar). */
  className?: string
  /** Show logo/title block (sidebar + drawer). */
  showBrand?: boolean
}

export function SidebarNavContent({ onNavigate, className = '', showBrand = true }: SidebarNavContentProps) {
  const { user, logout } = useAuth()
  const { t } = useI18n()
  const location = useLocation()

  const initials = (user?.username || user?.email || 'U').slice(0, 2).toUpperCase()
  const displayName =
    (user as { fullName?: string })?.fullName || user?.username || user?.email || t('common.user', 'User')
  const roleLabel = String(user?.role || '') || t('common.user', 'User')
  const role = String(user?.role || 'user').toLowerCase()
  const isAdmin = role === 'admin'
  const perms = Array.isArray((user as { permissions?: string[] })?.permissions)
    ? (user as { permissions: string[] }).permissions
    : []

  const filteredMenu = menuConfig
    .filter((item) => item.roles.includes(isAdmin ? 'admin' : 'user'))
    .filter((item) => isAdmin || !item.permission || perms.includes(item.permission))

  const handleNav = () => {
    onNavigate?.()
  }

  return (
    <div
      className={`sidebar-nav-content flex flex-col min-h-0 flex-1 ${className}`.trim()}
    >
      {showBrand && (
        <div className="sidebar-nav-content__brand header">
          <span className="header__full">DefendSphere</span>
          <span className="header__short" aria-hidden="true">
            DS
          </span>
        </div>
      )}
      <nav className="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto">
        {filteredMenu.map(({ to, label, icon: Icon }) => {
          let displayLabel = label
          if (label === 'TPSM') {
            displayLabel = t('tpsm.title', 'Third-Party Security Management')
          } else if (label === 'Compliance Center') {
            displayLabel = t('compliance.center.title', 'Compliance Center')
          }
          const isTPSMActive = to === '/tpsm/inventory' && location.pathname.startsWith('/tpsm')
          const isComplianceCenterActive = to === '/compliance-center/overview' && (
            location.pathname.startsWith('/compliance-center') ||
            location.pathname.startsWith('/governance') ||
            location.pathname === '/compliance'
          )
          const navEnd = to === '/' || to === '/admin'
          return (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                [
                  'flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-300 text-white nav-link-row',
                  isActive || isTPSMActive || isComplianceCenterActive ? 'bg-sidebarActive active' : 'hover:bg-sidebarHover',
                ].join(' ')
              }
              end={navEnd}
              onClick={handleNav}
            >
              <Icon className="w-5 h-5 flex-shrink-0 text-white" aria-hidden />
              <span className="nav-label min-w-0">{displayLabel}</span>
            </NavLink>
          )
        })}
      </nav>
      <div className="user-block">
        <div className="user-block__identity">
          <div className="avatar flex-shrink-0">{initials || 'U'}</div>
          <div className="user-block__text min-w-0">
            <div className="user-block__name truncate">{displayName}</div>
            <div className="role user-block__role truncate">{roleLabel}</div>
          </div>
        </div>
        <Button
          type="button"
          color="outline"
          iconOnly
          className="logout border-0 bg-transparent p-0 shadow-none hover:bg-transparent flex-shrink-0"
          title={t('auth.logout', 'Logout')}
          aria-label={t('auth.logout', 'Logout')}
          onClick={() => {
            logout()
            onNavigate?.()
          }}
          icon={<LogOut className="w-5 h-5 text-white" aria-hidden />}
        >
          {null}
        </Button>
      </div>
    </div>
  )
}
